import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import Button from '../../components/button';
import RadioButtons from '../../components/radio_buttons';
import BluefinForm from '../../components/autopayments_bluefin_form';
import EzidebitForm from '../../components/autopayments_ezidebit_form';
import GocardlessForm from '../../components/autopayments_gocardless_form';
import IntegrapayForm from '../../components/autopayments_integrapay_form';
import PaymentProviderBadge from '../../components/payment_provider_badge';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import currencyFormatter from 'helpers/currency_formatter';

const paymentProviderOptions = [
  ['ezidebit', 'Ezidebit'],
  ['bluefin', 'Bluefin'],
  ['stripe', 'Stripe'],
  ['integrapay', 'Worldpay'],
  ['gocardless', 'GoCardless'],
];

const GymProfileAutopayments = ({ getProfile }) => {
  const [profile, setProfile] = useState<any>({});
  const [state, setState] = useState<any>({});
  const [gocardlessDetail, setGocardlessDetail] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const loadingAnimation = useAngularService('loadingAnimation');
  const $http = useAngularService('$http');
  const $rootScope = useAngularService('$rootScope');
  const GTMService = useAngularService('GTMService');
  const IntercomService = useAngularService('IntercomService');
  const AvailablePaymentProvidersService = useAngularService('AvailablePaymentProvidersService');
  const gym = $rootScope.gym;

  useEffect(() => {
    GTMService.trackPage('Account Profile - Auto Payments');
    if (gym.uses_gocardless) {
      $http.get(`gyms/${gym.id}/gocardless_detail`).then((response) => {
        setGocardlessDetail(response.data);
      });
    }

    getProfile().then((response) => {
      setProfile(response.data);
    });
  }, []);

  const providers = () => {
    const defaultProviders = AvailablePaymentProvidersService.get();
    if (
      gym.gym_config.enable_stripe_membership_payments_override &&
      defaultProviders.indexOf('stripe') < 0
    ) {
      defaultProviders.push('stripe');
    }
    return defaultProviders;
  };

  const getAvailableProviders = () => {
    return _.reject(
      providers(),
      (item) =>
        (item === 'stripe' && gym.uses_stripe) ||
        (item === 'gocardless' && gym.uses_gocardless) ||
        (item === 'integrapay' && gym.uses_integrapay) ||
        (item === 'ezidebit' && gym.uses_ezidebit) ||
        (item === 'bluefin' && gym.uses_bluefin),
    );
  };

  const [availablePaymentProviders] = useState(getAvailableProviders());
  const [PSAForm, setPSAForm] = useState({
    payment_provider: availablePaymentProviders[0],
  });

  const payment_system_account = gym.payment_system_account || {};
  const secondary_payment_system_account = gym.secondary_payment_system_account || {};

  const isSecondaryPaymentProviderAvailable = () => {
    return (
      gym.country === 'GB' &&
      gym.payment_system_account?.id &&
      !gym.secondary_payment_system_account?.id &&
      availablePaymentProviders.length
    );
  };

  const displayPaymentProviderForm = () =>
    !payment_system_account?.id || state.displaySecondaryProviderForm;

  const addSecondaryProvider = () => {
    setState({ ...state, displaySecondaryProviderForm: true });
  };

  const isPaymentSystemsAvailable = () => !!providers().length;

  const isPaymentSystemAvailable = (name) => availablePaymentProviders.indexOf(name) >= 0;

  const isPaymentSystemRadioButtonsVisible = () =>
    !payment_system_account?.id && availablePaymentProviders.length > 1;

  const save = () => {
    const url = `gyms/${gym.id}`;
    loadingAnimation.show();
    const params: any = {};
    if (state.displaySecondaryProviderForm) {
      params.secondary_payment_system_account_attributes = PSAForm;
    } else {
      params.payment_system_account_attributes = PSAForm;
    }
    GTMService.trackPage('Account Profile - Auto Payments', 'attempt');

    $http.put(url, { gym: params }).then(
      () => {
        GTMService.trackPage('Account Profile - Auto Payments', 'success');
        const provider = PSAForm.payment_provider;
        IntercomService.track(`${provider}-account-linked`);
        notification.success('Saved successfully');
        window.location.reload();
      },
      (response) => {
        GTMService.trackPage('Account Profile - Auto Payments', 'error');
        notification.error('Save failed. Please fix the errors below');
        setErrors(response.data.errors);
        loadingAnimation.hide();
      },
    );
  };

  if (!profile.gym_contact_detail_attributes) return null;

  return (
    <div className="content-box-mini">
      {isPaymentSystemsAvailable() ? (
        <div className="row">
          <div className="col-sm-12">
            {!isPaymentSystemRadioButtonsVisible() ? (
              <h4 className="light first-one">Payment Provider for member autopayments</h4>
            ) : null}

            <div className="row">
              {payment_system_account.id ? (
                <PaymentProviderBadge
                  account={payment_system_account}
                  currency={gym.currency_symbol}
                  gocardlessDetail={gocardlessDetail}
                  provider={gym.payment_system}
                />
              ) : null}

              {secondary_payment_system_account.id ? (
                <PaymentProviderBadge
                  account={secondary_payment_system_account}
                  currency={gym.currency_symbol}
                  gocardlessDetail={gocardlessDetail}
                  provider={gym.secondary_payment_system}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <h4>
            Sorry!
            <br />
            We don't currently support any payment providers in your country.
          </h4>
          <p></p>
        </div>
      )}

      {isSecondaryPaymentProviderAvailable() && !state.displaySecondaryProviderForm ? (
        <div>
          <br />
          <Button variant="primary" onClick={() => addSecondaryProvider()}>
            Add another payment provider
          </Button>
        </div>
      ) : null}

      {isPaymentSystemRadioButtonsVisible() ? (
        <div className="select-payment-system">
          <h4 className="first-one light">Choose provider for autopayments</h4>
          <RadioButtons
            options={paymentProviderOptions.filter((item) => isPaymentSystemAvailable(item[0]))}
            getLabel={(item) => item[1]}
            getValue={(item) => item[0]}
            onChange={(item) => setPSAForm({ ...PSAForm, payment_provider: item })}
            value={PSAForm.payment_provider}
          />
        </div>
      ) : null}

      {displayPaymentProviderForm() ? (
        <div>
          {PSAForm.payment_provider == 'stripe' ? (
            <div className="payment-provider">
              <div className="payment-provider-logo">
                <img alt="Stripe" src="/stripe.png" />
              </div>
              {profile.default_stripe_application_fee > 0 ? (
                <p className="stripe-note-fee">
                  <strong>Please note:</strong> An additional application fee of{' '}
                  <strong>
                    {currencyFormatter(profile.default_stripe_application_fee, gym.currency_symbol)}{' '}
                    per transaction
                  </strong>{' '}
                  will be applied to all Stripe payments processed through your Clubworx account.
                </p>
              ) : null}

              <a className="btn btn-success" href={profile.stripe_connect_url} target="_self">
                Connect Stripe Account
              </a>
            </div>
          ) : null}

          {PSAForm.payment_provider == 'bluefin' && (
            <BluefinForm
              errors={errors}
              paymentSystemAccount={PSAForm}
              setForm={setPSAForm}
              save={save}
            />
          )}

          {PSAForm.payment_provider == 'ezidebit' && (
            <EzidebitForm
              errors={errors}
              paymentSystemAccount={PSAForm}
              setForm={setPSAForm}
              save={save}
            />
          )}
          {PSAForm.payment_provider == 'integrapay' && (
            <IntegrapayForm
              errors={errors}
              paymentSystemAccount={PSAForm}
              setForm={setPSAForm}
              save={save}
            />
          )}
          {PSAForm.payment_provider == 'gocardless' && (
            <GocardlessForm applicationFee={profile.default_gocardless_application_fee} />
          )}
        </div>
      ) : null}
    </div>
  );
};

angular.module('gym').value('GymProfileAutopayments', GymProfileAutopayments);
